/**
 * Product Page VAT Status Dropdown
 * Syncs VAT status selection with header VAT toggle
 * and applies VAT inc/ex pricing across the site.
 */

(function() {
  'use strict';

  const VAT_STORAGE_KEY = 'bentley_vat_setting';
  const TAX_EXEMPT_STORAGE_KEY = 'bentley_tax_exempt';
  const DEFAULT_VAT = 'inc';
  const DEFAULT_VAT_RATE = 20;

  /**
   * Initialize VAT-driven price display on all pages
   * (home, collection, product, etc.).
   */
  function initVatPriceDisplay() {
    // Determine initial VAT setting
    let vatValue = DEFAULT_VAT;

    if (typeof window.getCurrentVatSetting === 'function') {
      vatValue = window.getCurrentVatSetting();
    } else if (window.localStorage) {
      vatValue = localStorage.getItem(VAT_STORAGE_KEY) || DEFAULT_VAT;
    }


    // Ensure header/settings VAT toggles reflect the current setting
    updateAllVatToggles(vatValue);

    applyVatToAllPrices(vatValue);

    // Update all VAT indicator text
    updateAllVatIndicators(vatValue);

    // Listen for global VAT changes (from header dropdown, product select, cart tax-exempt, etc.)
    document.addEventListener('vatChanged', function(e) {
      const newVat = e && e.detail && e.detail.vat ? e.detail.vat : null;
      const valueToUse = newVat || vatValue || DEFAULT_VAT;

      // Keep UI toggles and prices in sync
      updateAllVatToggles(valueToUse);
      applyVatToAllPrices(valueToUse);
      updateAllVatIndicators(valueToUse);
    });
  }

  /**
   * Update all VAT indicator text elements on the page
   * @param {string} vatValue - The VAT value ('ex' or 'inc')
   */
  function updateAllVatIndicators(vatValue) {
    const indicators = document.querySelectorAll('.js-vat-indicator');
    const text = vatValue === 'inc' ? '(incl VAT)' : '(ex VAT)';
    indicators.forEach(function(indicator) {
      indicator.textContent = text;
    });
  }

  /**
   * Initialize product page VAT status dropdown
   */
  function initProductVatStatus() {
    const vatStatusSelect = document.getElementById('vat-status-select');

    if (!vatStatusSelect) {
      return;
    }

    // Load saved VAT setting from local storage
    const savedVat = (window.localStorage && localStorage.getItem(VAT_STORAGE_KEY)) || DEFAULT_VAT;
    vatStatusSelect.value = savedVat;

    // Handle VAT status change from the product dropdown
    vatStatusSelect.addEventListener('change', function() {
      const vatValue = this.value;

      // Save to local storage (sync both VAT and tax exempt settings)
      if (window.localStorage) {
        localStorage.setItem(VAT_STORAGE_KEY, vatValue);
        localStorage.setItem(TAX_EXEMPT_STORAGE_KEY, vatValue === 'ex' ? 'yes' : 'no');
      }

      // Update all VAT toggle displays across the page
      updateAllVatToggles(vatValue);

      // Apply VAT to all prices immediately
      applyVatToAllPrices(vatValue);

      // Dispatch custom event for other scripts to listen to
      const event = new CustomEvent('vatChanged', { detail: { vat: vatValue } });
      document.dispatchEvent(event);
    });

    // Keep product dropdown in sync when VAT is changed elsewhere
    document.addEventListener('vatChanged', function(e) {
      if (e.detail && e.detail.vat) {
        vatStatusSelect.value = e.detail.vat;
      }
    });
  }

  /**
   * Update all VAT toggle displays (header, sticky header, mobile)
   */
  function updateAllVatToggles(vatValue) {
    // Update standalone VAT toggle box (next to sign in/register)
    const standaloneToggle = document.querySelector('.vat-toggle-box .tt-dropdown-toggle');
    if (standaloneToggle) {
      updateToggleDisplay(standaloneToggle, vatValue);
    }

    // Update VAT toggles in settings dropdowns (main header and sticky header)
    const settingsDropdowns = document.querySelectorAll('.vat-toggle-dropdown');
    settingsDropdowns.forEach(dropdown => {
      updateSettingsDropdownDisplay(dropdown, vatValue);
    });
  }

  /**
   * Update standalone toggle display text
   */
  function updateToggleDisplay(dropdownToggle, vatValue) {
    const textSpan = dropdownToggle.querySelector('.tt-text');
    if (textSpan) {
      if (vatValue === 'inc') {
        textSpan.textContent = 'Inc. Vat';
      } else {
        textSpan.textContent = 'Ex. Vat';
      }
    }
  }

  /**
   * Update settings dropdown display to highlight selected VAT option
   */
  function updateSettingsDropdownDisplay(dropdown, vatValue) {
    const vatOptions = dropdown.querySelectorAll('.vat-option');
    vatOptions.forEach(option => {
      if (option.getAttribute('data-vat') === vatValue) {
        option.classList.add('active');
      } else {
        option.classList.remove('active');
      }
    });
  }

  /**
   * Helpers for VAT-aware price calculation
   */

  function parsePrice(value) {
    const intVal = parseInt(value, 10);
    return isNaN(intVal) ? null : intVal;
  }

  function getVatRateForElement(el) {
    const attr = el.getAttribute('data-vat-rate');
    const rate = attr ? parseFloat(attr) : NaN;
    return isNaN(rate) ? DEFAULT_VAT_RATE : rate;
  }

  // Check whether Shopify prices include VAT (set in theme.liquid from shop.taxes_included)
  function shopPricesIncludeVat() {
    return typeof window.SHOP_PRICES_INCLUDE_VAT !== 'undefined' && window.SHOP_PRICES_INCLUDE_VAT === true;
  }

  // Calculate the correct price based on VAT setting
  // If shop prices include VAT: show as-is for "inc", remove VAT for "ex"
  // If shop prices exclude VAT: add VAT for "inc", show as-is for "ex"
  function calculateVatPrice(basePrice, vatRate, vatSetting) {
    if (basePrice == null) return null;

    const multiplier = 1 + (vatRate / 100);
    const pricesIncludeVat = shopPricesIncludeVat();

    if (pricesIncludeVat) {
      // Shopify prices already include VAT
      if (vatSetting === 'inc') {
        // "Inc VAT" mode: show the stored price as-is (it already includes VAT)
        return basePrice;
      }
      // "Ex VAT" mode: remove VAT from the price
      return Math.round(basePrice / multiplier);
    } else {
      // Shopify prices are net (exclude VAT)
      if (vatSetting === 'inc') {
        // "Inc VAT" mode: add VAT to the stored net price
        return Math.round(basePrice * multiplier);
      }
      // "Ex VAT" mode: show the stored net price as-is
      return basePrice;
    }
  }

  function applyVatToPriceElement(el, vatSetting) {
    const netPrice = parsePrice(el.getAttribute('data-net-price'));
    if (netPrice == null) {
      return;
    }

    const netCompare = parsePrice(el.getAttribute('data-net-compare-price'));
    const vatRate = getVatRateForElement(el);

    const mainPriceCents = calculateVatPrice(netPrice, vatRate, vatSetting);
    const comparePriceCents = netCompare != null
      ? calculateVatPrice(netCompare, vatRate, vatSetting)
      : null;

    const mainPriceSpan = el.querySelector('.new-price, .sale-price') || el.querySelector('span:not(.old-price)');
    const oldPriceSpan = el.querySelector('.old-price');

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      if (mainPriceSpan && mainPriceCents != null) {
        mainPriceSpan.innerHTML = Shopify.formatMoney(mainPriceCents, money_format);
      }

      const hasSale = netCompare != null && netCompare > netPrice;

      if (oldPriceSpan) {
        if (hasSale && comparePriceCents != null && comparePriceCents > mainPriceCents) {
          oldPriceSpan.innerHTML = Shopify.formatMoney(comparePriceCents, money_format);
          oldPriceSpan.classList.remove('hide');

          // Ensure main price has sale class for styling if needed
          if (!mainPriceSpan.classList.contains('new-price')) {
            mainPriceSpan.classList.add('new-price');
          }
        } else {
          oldPriceSpan.classList.add('hide');
        }
      }
    }

    // Keep track of the last applied prices (useful for debugging/other scripts)
    if (mainPriceCents != null) {
      el.setAttribute('data-current-price', String(mainPriceCents));
    }
    if (comparePriceCents != null) {
      el.setAttribute('data-current-compare-price', String(comparePriceCents));
    }
  }

  function applyVatToAllPrices(vatSetting) {
    const setting = vatSetting || DEFAULT_VAT;
    const priceElements = document.querySelectorAll('.js-vat-price, .tt-price[data-net-price]');

    priceElements.forEach(el => {
      applyVatToPriceElement(el, setting);
    });
  }

  // Expose for use from other scripts (e.g., swatchVariantHandler)
  window.applyVatToPrices = function(vatSetting) {
    let setting = vatSetting;

    if (!setting) {
      if (typeof window.getCurrentVatSetting === 'function') {
        setting = window.getCurrentVatSetting();
      } else if (window.localStorage) {
        setting = localStorage.getItem(VAT_STORAGE_KEY) || DEFAULT_VAT;
      } else {
        setting = DEFAULT_VAT;
      }
    }

    applyVatToAllPrices(setting);
  };

  // Initialize when DOM is ready
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function() {
    initVatPriceDisplay();
    initProductVatStatus();

    // Re-apply VAT pricing whenever the theme refreshes currency (e.g., after AJAX loads)
    if (window.jQuery && typeof jQuery === 'function') {
      jQuery('body').on('refreshCurrency', function() {
        if (typeof window.applyVatToPrices === 'function') {
          window.applyVatToPrices();
        }
      });
    }
  });
})();

